import CircularProgress from '@/components/CircularProgress';
import Loader from '@/components/Loader';
import StudentConfirmDialog, { StudentConfirmDialogPropsType } from '@/components/StudentConfirmDialog';
import Constants from '@/Constants';
import { useQuestionnaire } from '@/hooks/questionnaire/useQuestionnaire';
import { AnswerResult } from '@/models/Question';
import { Questionnaire } from '@/models/Questionnaire';
import { ApplicationState } from '@/store';
import { clearQuestionnaireStore, getQuestionnaireResponseSummaryRequest, getQuestionRequest } from '@/store/questionnaire/action';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import AnsweredQuestionModal from './AnsweredQuestionModal';
import './style.scss';

export interface QuestionnaireFeedbackProps {
    masteryScore: number;
    score: number;
    questionnaireId: number;
    attemptsLeft: number;
    classEnrollmentId: number;
    courseId: number;
    lessonId: number;
    showResponseSummary: boolean;
    onQuestionChange?: () => void;
    fullScreen?: boolean;
}

const QuestionnaireFeedback = ({
    masteryScore,
    score,
    questionnaireId,
    attemptsLeft,
    classEnrollmentId,
    courseId,
    lessonId,
    showResponseSummary,
    onQuestionChange,
    fullScreen = false,
}: QuestionnaireFeedbackProps) => {
    const dispatch = useDispatch();
    const history = useHistory();

    const { responseSummary, question, strings, person, loading, hasPassed } = useSelector((state: ApplicationState) => ({
        question: state.questionnaire.question,
        responseSummary: state.questionnaire.responseSummary,
        loading: state.questionnaire.loading,
        strings: state.locale.data,
        person: state.identity.data.person,
        hasPassed: score >= masteryScore,
    }));

    const [showAnswerModal, setShowAnswerModal] = useState<boolean>(false);
    const [modalOptionIds, setModalOptionIds] = useState<number[]>([]);
    const [modalQuestionIndex, setModalQuestionIndex] = useState<number>(1);
    const [showConfirmationModal, setShowConfirmationModal] = useState<boolean>(false);
    const [modalConfig, setModalConfig] = useState<StudentConfirmDialogPropsType>({} as StudentConfirmDialogPropsType);
    const [adjustFeedbackWidth, setAdjustFeedbackWidth] = useState<boolean>(false);

    const { questionnaire = {} as Questionnaire, revalidateResult } = useQuestionnaire(questionnaireId);

    useEffect(() => {
        if (questionnaire.score === null || (questionnaire.score === undefined && questionnaireId > 0)) {
            revalidateResult();
        }

        if (showResponseSummary && questionnaireId) {
            dispatch(getQuestionnaireResponseSummaryRequest(questionnaireId));
        }
    }, [questionnaireId, questionnaire.score, showResponseSummary]);

    const clickOnQuestionHandler = (answerResult: AnswerResult, index: number) => {
        dispatch(getQuestionRequest(questionnaireId, answerResult.questionId));

        if (questionnaire.answers) {
            const answer = questionnaire.answers.find((a) => a.question.id === answerResult.questionId);

            if (answer?.answerJSON) {
                setModalOptionIds(answer?.answerJSON?.answer.filter(({ data }) => !!data).map(({ optionId }) => optionId) || []);
            } else if (answer?.answerOption) {
                setModalOptionIds([answer?.answerOption.id]);
            } else {
                setModalOptionIds([]);
            }

            setModalQuestionIndex(index);
        }

        if (answerResult.questionId === question.id) {
            setShowAnswerModal(true);
        }
    };

    useEffect(() => {
        if (question.id) {
            setShowAnswerModal(true);
        }
    }, [question.id]);

    const onTryAgainClickHandler = () => {
        setModalConfig({
            leftButtonTitle: strings.EXIT_EVALUATION,
            leftClick: () => navigateToCourse(),
            rightButtonTitle: strings.QUESTIONNAIRE_RESULTS.TRY_AGAIN,
            rightClick: () => navigateToQuestionnaire(),
            title: strings.QUESTIONNAIRE_RESULTS.YOU_STILL_HAVE_X_ATTEMPTS.replace('%n', String(attemptsLeft)),
            content: strings.QUESTIONNAIRE_RESULTS.DO_YOU_WANT_TO_RETRY,
        } as StudentConfirmDialogPropsType);
        setShowConfirmationModal(true);
    };

    const navigateToQuestionnaire = () => {
        setShowConfirmationModal(false);
        dispatch(clearQuestionnaireStore());
        history.replace(`/lms/enrollments/${classEnrollmentId}/courses/${courseId}/${Constants.EVALUATIONS_RESOURCE_NAME}/${lessonId}`);
    };

    const navigateToCourse = () => {
        setShowConfirmationModal(false);
        dispatch(clearQuestionnaireStore());
        history.goBack();
    };

    const removeHtmlTags = (value: string): string => {
        return value.replace(/<[^>]*>?/gm, '');
    };

    useEffect(() => {
        return () => {
            dispatch(clearQuestionnaireStore());
        };
    }, []);

    return (
        <div className="questionnaire-feedback">
            {/* TODO: Aguardando deploy do release 052022-1 sem o Toast */}
            {/* <NextLessonToast
                containerStyle={{ marginTop: 100 }}
                onToastChange={({ isAvailable, isCollapsed }) => {
                    setAdjustFeedbackWidth(isAvailable && isCollapsed);
                }}
            /> */}
            <StudentConfirmDialog
                content={modalConfig.content}
                leftButtonTitle={modalConfig.leftButtonTitle}
                leftClick={() => modalConfig && modalConfig.leftClick && modalConfig.leftClick()}
                rightButtonTitle={modalConfig.rightButtonTitle}
                rightClick={() => modalConfig.rightClick()}
                show={showConfirmationModal}
                title={modalConfig.title}
            />
            <div className="left-section">
                {loading && score === null ? (
                    <Loader />
                ) : (
                    <div className="d-flex flex-column align-items-center">
                        <div>
                            {`${hasPassed ? strings.QUESTIONNAIRE_RESULTS.PASSED_MSG : strings.QUESTIONNAIRE_RESULTS.NOT_PASSED_MSG} ${
                                person.firstName
                            }. ${strings.QUESTIONNAIRE_RESULTS.YOU_WERE}:`}
                        </div>
                        <div className="questionnaire-result">
                            {hasPassed ? strings.QUESTIONNAIRE_RESULTS.MALE_APPROVED : strings.QUESTIONNAIRE_RESULTS.MALE_NOT_APPROVED}
                        </div>

                        <CircularProgress className="mt-3 mb-3" isBelow={!hasPassed} percent={score || 0} />

                        <div>{strings.QUESTIONNAIRE_RESULTS.MESSAGE_LINE_1.replace('{0}', masteryScore + '').replace('{1}', score + '')}</div>
                        {showResponseSummary && <div>{strings.QUESTIONNAIRE_RESULTS.MESSAGE_LINE_2}</div>}
                        <div>
                            {attemptsLeft > 0 && !hasPassed && (
                                <button className="btn btn-primary btn-sm try-again-button" onClick={() => onTryAgainClickHandler()} type="button">
                                    {strings.QUESTIONNAIRE_RESULTS.TRY_AGAIN}
                                </button>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {showResponseSummary && responseSummary && responseSummary.length > 0 && (
                <div className="right-section flex-grow-1">
                    <div className={`right-section__container ${adjustFeedbackWidth ? 'reduced' : ''}`}>
                        <div className="right-section__title">
                            <strong>{strings.QUESTIONNAIRE_RESULTS.RESPONSE_SUMMARY}</strong>
                        </div>

                        {responseSummary.length > 0 &&
                            responseSummary.map((r: AnswerResult, index: number) => (
                                <div className="right-section__question" key={index} onClick={() => clickOnQuestionHandler(r, index + 1)}>
                                    <span>{index + 1}</span>
                                    <span dangerouslySetInnerHTML={{ __html: removeHtmlTags(r.questionDescription) }}></span>
                                    <span className={`material-icons ${r.correct ? 'green' : 'red'}`}>
                                        {r.correct ? Constants.icons.VALID_CHECK : Constants.icons.INVALID_CROSS}
                                    </span>
                                </div>
                            ))}
                    </div>

                    {question && question.id && (
                        <>
                            <AnsweredQuestionModal
                                modalOptionIds={modalOptionIds}
                                modalQuestionIndex={modalQuestionIndex}
                                onClickImage={onQuestionChange}
                                setShow={setShowAnswerModal}
                                show={showAnswerModal && !fullScreen}
                            />
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

export default QuestionnaireFeedback;
